import Controller from '@ember/controller';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import { getUploadFn, SignResponse } from 'cio/utils/uploads';
import SnackbarService from 'cio/services/snackbar';

const SNACKBAR_UPLOAD_KEY = 'support-assets.upload';

interface FileObject {
  url: string;
  fileName: string;
}

// Exporting this function for testing purposes.
export function sanitizeHarFile(content: string) {
  let functions: ((content: string) => string)[] = [removeBearerTokens];
  for (let func of functions) {
    content = func(content);
  }

  return content;
}

function findBearerTokens(content: string) {
  let bearerTokenRegex = /Bearer ([\d\w-]+)/g;
  let matches = content.matchAll(bearerTokenRegex);
  let tokens = new Set<string>();

  for (let match of matches) {
    let [, token] = match;
    if (token != null) {
      tokens.add(token);
    }
  }

  return tokens.size === 0 ? [] : Array.from(tokens);
}

function removeBearerTokens(content: string) {
  let tokens = findBearerTokens(content);
  for (let token of tokens) {
    // @ts-expect-error String.replaceAll is on es2021+ but we're using es2020.
    content = content.replaceAll(token, 'REDACTED');
  }

  return content;
}

export default class HarSanitizerController extends Controller {
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service snackbar!: SnackbarService;

  queryParams = ['ticketId'];
  fileReader: FileReader;

  @tracked ticketId: string | null = null;
  @tracked originalFile: File | null = null;
  @tracked sanitizedFile: File | null = null;
  @tracked downloadFile: FileObject | null = null;
  @tracked isUploadModalVisible = false;
  @tracked uploadErrorMessages: string[] | null = null;

  constructor() {
    super(...arguments);
    this.fileReader = new FileReader();
    this.fileReader.addEventListener('load', this.setSanitizedFile);
  }

  get canAcceptUpload() {
    // note: `url.com?ticketId` results in the query param `ticketId` equaling `true`
    return this.ticketId != null && !isNaN(Number(this.ticketId));
  }

  get uploadButtonText() {
    if (this.uploadTask.isRunning) {
      return 'Uploading…';
    }

    return this.downloadFile == null ? 'Upload HAR file' : 'Upload another HAR file';
  }

  @action
  toggleUploadModalVisibility() {
    this.isUploadModalVisible = !this.isUploadModalVisible;

    if (this.isUploadModalVisible) {
      this.resetFiles();
    }
  }

  @action
  handleFileSelected(file: File) {
    this.snackbar.remove(SNACKBAR_UPLOAD_KEY);

    this.originalFile = file;
    this.fileReader.readAsText(file as Blob);
  }

  @action
  onClickUpload() {
    if (this.canAcceptUpload) {
      this.uploadTask.perform();
    } else {
      this.setDownloadFile();
      this.isUploadModalVisible = false;
    }
  }

  @action
  setSanitizedFile(event: ProgressEvent<FileReader>) {
    let content = (event.target?.result as string) ?? '';
    let sanitizedContent = sanitizeHarFile(content);
    let newFileName = this.originalFile!.name.replace('.har', '-sanitized.har');
    this.sanitizedFile = new File([sanitizedContent], newFileName, { type: 'application/json' });
  }

  @action
  setDownloadFile() {
    if (this.sanitizedFile == null) {
      return;
    }

    this.downloadFile = {
      url: URL.createObjectURL(this.sanitizedFile as Blob),
      fileName: this.sanitizedFile.name,
    };
  }

  uploadTask = task(async () => {
    if (this.sanitizedFile == null || this.ticketId == null) {
      return;
    }

    let file = this.sanitizedFile;
    this.uploadErrorMessages = null;

    try {
      let json: SignResponse & Record<string, any> = await this.api.post(`support/sign`, {
        ignoreErrors: true,
        pathScope: 'account',
        data: {
          name: file.name,
          size: String(file.size),
          type: file.type,
          reference: this.ticketId.trim(),
        },
      });

      let { uploadFn, data } = getUploadFn(json, file.type);
      await uploadFn(file, data, {});

      this.isUploadModalVisible = false;

      this.snackbar.add(SNACKBAR_UPLOAD_KEY, 'HAR file successfully sanitized and uploaded');
      this.resetFiles();
    } catch (ex) {
      let errorStatus: string = ex?.errors?.[0]?.status ?? '';
      switch (errorStatus) {
        case '429':
          this.uploadErrorMessages = [
            'For security reasons, we limit how often files can be uploaded. Please try again later.',
          ];
          return;
        case '400':
        case '422':
          this.uploadErrorMessages = ex?.errors?.map((error: any) => error.detail);
          if (this.uploadErrorMessages != null) {
            return;
          }
      }

      this.apiErrors.report(ex);
    }
  });

  @action
  resetFiles() {
    this.uploadErrorMessages = null;
    this.originalFile = null;
    this.sanitizedFile = null;
    this.downloadFile = null;
  }
}
